import { TPipelineView } from '~/apps/main/Pipeline/pipeline.types';
import { action, computed, observable, set } from 'mobx';
import colors from '@honeybook/hbui/dist/styles/colors';
import {
    IClientPortalConfiguration,
    IClientPortalTheme,
    ICompanyFont,
    IContactForm,
    ICustomProjectField,
    IFileAsset,
    IFileTemplate,
    IFlowTemplate,
    IFlowTheme,
    IFolder,
    IImage,
    IOwner,
    IQuickbooksIntegration,
    ITeamMember,
    IVendorEmail,
    IVendorPackage,
    TFlowTag,
    TSmsType,
    IInvoice,
    IAgreement,
    IProposal
} from '~/types/model.types';
import {
    COMPANY_OWNER_NOTIFICATION_TYPE_TO_CD_MAP,
    CompanyCustomDomainResponse,
    ICompanyOwnerNotification,
    ICompanyResponse,
    ICompanySettings,
    ILeadSource,
    InquiryFollowUpResponse,
    IProjectType as IServerProjectType,
    ISessionConfig,
    IStripePerson,
    ISuggestedAssets,
    ITag,
    IUserPermissions,
    IUserPipelineStageResponse,
    IWorkflowAutomationInfo,
    TCompanyOwnerNotificationType,
    TCompanyTypes,
    TCompanyVertical,
    TeamRole,
    ICompanySmsPreferences,
    ITimelineContainerResponse
} from '~/types/server.types';
import { AtoZcomparator } from '~/utils/automation.utils';
import { processEmailForEdit } from '~/utils/emails.utils';
import { sortProjectTypes } from '~/components/common/projectTypes.utils';
import { IProjectType } from '~/components/CompanySettings/ProjectTypes/EditProjectTypesModal';
import {
    Currency,
    EventTypes,
    WorkflowAutomationType
} from '~/types/enums.types';
import { extend, kebabCase, last } from 'lodash';
import type { AuthenticatedGlobalContextValue } from '~/providers/base-global-context/authenticated-global.context';
import { CompanyTypeKeyEnum } from '~/utils/company.utils';
import {
    shortTimezoneToFullTimezone,
    TFullTimezone,
    TShortTimezone
} from '~/utils/timezone.utils';
import { ICustomFieldSchema } from '~/components/CustomFields/custom-field-schemas.types';
import {
    TCompanySpaceUpdate,
    TPayoutDelayReason
} from '~/apps/main/CompanySettings/CompanyPreferences/server-types';
import { CompanyController } from '~/controllers/company.controller';
import { TCustomFont } from '@honeybook/hbui/dist/styles/fonts';
import { TAvailabilityType } from '~/apps/main/Calendar/utils/calendar-page.types';
import { tryParseToJsonString } from '~/utils/json.utils';

export const INITIAL_COMPANY_FLOW_THEME: IFlowTheme = {
    styles: {
        font_family: 'ProximaNova',
        background_color: colors.white,
        color: colors.neutral900
    },
    buttons_styles: {
        font_family: 'ProximaNova',
        size: 'md',
        border_radius: 10,
        color: colors.white
    },
    questions_styles: {
        font_family: 'ProximaNova',
        size: 'md',
        color: colors.neutral900,
        warning_color: colors.red50,
        background_color: colors.neutral100,
        border_color: colors.transparent,
        border_radius: 10
    }
};

export type MinimalCompany = Pick<
    Company,
    '_id' | 'email' | 'company_name' | 'icon_image'
>;

export type UpdateClientPortalBranding = {
    accent_color: string;
    sidebar_color: string;
    button_border_radius: number;
    button_font: TCustomFont;
    heading_font: TCustomFont;
    project_background_color: string;
    button_text_color: string;
};

export type UpdatedClientPortalTheme = {
    logo_type: string;
    background_color: string;
    show_logo: boolean;
    title: string;
    client_portal_branding: UpdateClientPortalBranding;
};

export class Company {
    globalContext: AuthenticatedGlobalContextValue;

    _id: string;
    @observable company_name: string;
    @observable company_type: TCompanyTypes;
    other_company_type: string;
    company_vertical?: TCompanyVertical;
    is_msp?: boolean;
    company_type_name: string;
    owner?: IOwner;
    @observable team_members?: ITeamMember[];

    @observable description: string;
    @observable about: string;

    @observable phone_number: string;
    @observable email: string;
    @observable city: string;
    @observable state: string;
    @observable country: string;
    @observable street_address: string;
    @observable zip_code: string;
    website_url: string;
    @observable icon_image?: IImage;
    @observable logo_image: IImage;
    @observable twitter_url: string;
    @observable facebook_url: string;
    @observable instagram_url: string;
    @observable pinterest_url: string;
    @observable linkedin_url: string;
    @observable behance_url: string;
    @observable dribbble_url: string;
    @observable tiktok_url: string;
    @observable blog_url: string;
    @observable other_url: string;

    @observable images: IImage[];
    @observable default_cover_image_id: string;
    @observable text_color: string;
    @observable brand_color: string;
    @observable company_url_prefixes?: Array<string>;
    @observable company_setting_permissions?: TeamRole;
    company_url_domain: string;
    default_timezone: TShortTimezone;
    @observable vendor_emails: IVendorEmail[];
    @observable lead_sources: ILeadSource[];
    @observable project_types: IServerProjectType[];
    @observable tags: ITag[];
    @observable archive_reasons: string[];
    @observable service_units: string[];
    @observable default_project_type_label?: string;
    scheduler_url: string;
    schema_limit_per_entity: number;
    @observable custom_project_fields: ICustomProjectField[];
    @observable custom_project_fields_v2: ICustomFieldSchema[];
    @observable custom_contact_fields: ICustomFieldSchema[];
    @observable questionnaires: IFileTemplate[];
    @observable brochures: IFileTemplate[];
    @observable old_brochures: IFileTemplate[];
    @observable flows: IFlowTemplate[];
    block_templates: IFlowTemplate[];
    allow_ach_refund: boolean;
    allow_client_ach: boolean;
    @observable company_flow_theme: IFlowTheme;
    @observable folders: IFolder[];
    @observable company_fonts: ICompanyFont[];
    @observable vendor_packages: IVendorPackage[];
    @observable contact_forms: IContactForm[];
    @observable invoices: IInvoice[];
    @observable agreements: IAgreement[];
    @observable proposals: IProposal[];
    @observable scheduleSessionConfigs: ISessionConfig[];
    @observable sms_enabled: boolean;
    @observable client_2fa_sms_enabled: boolean;
    @observable bank_account_payment_method_id: string;
    fields_to_fix: string[];
    @observable is_flow_thumbnail_visible_to_client: boolean;
    is_non_event_type: boolean;
    subscribed?: boolean;
    subscribed_for_free?: boolean;
    pending_stripe_migration?: any;
    archived?: boolean;
    @observable user_permissions?: IUserPermissions[];
    membership_canceled?: boolean;
    allow_instant_payouts?: boolean;
    stripe_persons?: IStripePerson[];
    @observable suggested_assets?: ISuggestedAssets;
    @observable eligible_for_instant_payouts: boolean;
    @observable all_inclusive_instant_payouts: boolean;
    @observable eligible_for_finance_app_payments: boolean;
    @observable bank_account_user_id?: string;
    @observable currency?: Currency;
    @observable quickbooks_integration: IQuickbooksIntegration;
    @observable has_pictime_integration: boolean;
    @observable has_prismm_integration: boolean;
    @observable has_the_knot_integration: boolean;
    @observable client_portal_configuration?: IClientPortalConfiguration;
    @observable user_pipeline_stages?: IUserPipelineStageResponse[];
    hidden_project_fields: string[] | null;
    @observable fileAssets: IFileAsset[];
    @observable workflow_automation_infos?: IWorkflowAutomationInfo[] = [];
    @observable company_owner_notifications: ICompanyOwnerNotification[] = [];
    @observable company_settings: ICompanySettings = {
        _id: '',
        share_team_members_calendar: false
    };
    @observable n8n_user_initialized: boolean;
    show_membership_page?: boolean;
    @observable pipeline_views?: TPipelineView[];
    @observable inquiry_follow_up?: InquiryFollowUpResponse;
    @observable company_spaces?: TCompanySpaceUpdate[];
    @observable sms_payment_reminders_enabled?: boolean;
    @observable sms_meeting_reminders_enabled?: boolean;
    @observable contact_enrichment_enabled?: boolean;
    @observable ai_contact_followup_suggestions_enabled?: boolean;
    @observable contact_last_interaction_auto_update_enabled?: boolean;
    @observable default_agreement?: string;
    @observable first_created_contact_action_suggestions?: Date;
    @observable first_created_contact_action_suggestions_unresponsive?: Date;
    company_custom_domains: Array<CompanyCustomDomainResponse> = [];
    payout_delay_reason?: TPayoutDelayReason;
    @observable default_is_client_portal_exposed?: boolean;
    @observable default_company_availability: TAvailabilityType;
    @observable timeline_containers: ITimelineContainerResponse[] = [];

    @computed
    get currentCompanyUrlPrefix() {
        return last(this.company_url_prefixes) || kebabCase(this.company_name);
    }
    @observable is_powered_by_hb: boolean;

    constructor(
        company: ICompanyResponse,
        globalContext: AuthenticatedGlobalContextValue
    ) {
        this.globalContext = globalContext;

        // Initial company data get's loaded from local storage, but it's partial
        // This code fixes the case where local storage loading happens after fetching the
        // full company data from the server. We don't want to override the full company data
        // with the partial data from lohttps://honeybook.atlassian.net/browse/NG2REACT-822cal storage.
        // This is a temp solution, as we don't want to override the full company data at all.
        // TODO(REACT_FIRST): find a proper solution
        const filteredCompany = Object.entries(company).reduce(
            (acc, [key, value]) => {
                if (value !== undefined && value !== null) {
                    acc[key] = value;
                }
                return acc;
            },
            {} as Partial<ICompanyResponse>
        );

        set(this, filteredCompany);

        // TODO: why do we have images without a cloudinary_public_id? (I.E - vendor@vendor.com)
        // Do we need to support this type of images too?
        this.images = (company.cover_images || []).filter(
            img => !!img.cloudinary_public_id
        );
        this.fileAssets = company.company_assets || [];

        // TODO: need to have a method for extracting only primitives out of the class.
        this.owner = (company.owner?.dataOnly
            ? company.owner.dataOnly()
            : company.owner) || {
            _id: '',
            full_name: '',
            email: '',
            system_user_type: 'vendor',
            initials: ''
        };
        this.vendor_emails = company.vendor_emails || [];
        this.team_members = company.team_members || [];
        this.lead_sources = company.lead_sources || [];
        this.project_types = company.project_types || [];
        this.tags = company.tags || [];
        this.archive_reasons = company.archive_reasons || [];
        this.service_units = company.service_units || [];
        this.company_fonts = company.company_fonts || [];
        this.custom_project_fields = company.custom_project_fields || [];
        this.questionnaires = company.questionnaires || [];
        this.brochures = company.brochure_templates || [];
        this.old_brochures = company.brochure_containers || [];
        this.block_templates = company.block_templates || [];
        this.flows = company.flows || [];
        this.allow_ach_refund = company.allow_ach_refund;
        this.company_flow_theme =
            company.company_flow_theme || INITIAL_COMPANY_FLOW_THEME;
        this.custom_contact_fields = company.custom_contact_fields || [];
        this.folders = company.folders || [];
        this.user_pipeline_stages = company.user_pipeline_stages || [];
        this.pipeline_views = company.pipeline_views || [];
        this.company_spaces = company.company_spaces || [];
        this.timeline_containers = company.timeline_containers || [];
    }

    get hasAnySubscription() {
        return this.subscribed || this.subscribed_for_free;
    }

    @computed
    get inquiryStageLabel() {
        return (
            this.user_pipeline_stages.find(stage => stage.type === 'inquiry')
                ?.name || ''
        );
    }

    @computed
    get brandColor() {
        return this.brand_color || colors.blue20;
    }

    @computed
    get companyName() {
        return this.company_name;
    }

    @computed
    get companyFonts() {
        return this.company_fonts.filter(f => !f.is_deleted);
    }

    @computed
    get isNev() {
        return this.globalContext.configStore.config.non_event_company_type_enum?.includes(
            this.company_type
        );
    }

    @computed
    get teamMembers(): ITeamMember[] {
        return this.team_members.map(member => {
            return member.dataOnly ? member.dataOnly() : member;
        });
    }

    @computed
    get hasOtherCompanyMembers() {
        return this.getOtherCompanyMembers().length > 0;
    }

    @computed
    get hasBudgetField() {
        return !this.hidden_project_fields?.includes('budget');
    }

    @computed
    get hasGuestsField() {
        return !this.hidden_project_fields?.includes('guests');
    }

    getOtherCompanyMembers(teamMemberId?: string) {
        return this.allCompanyMembers.filter(member => {
            return teamMemberId
                ? member._id !== teamMemberId
                : member._id !== this.globalContext.userStore.currUser._id;
        }) as ITeamMember[];
    }

    @computed
    get enabledTeamMembers(): ITeamMember[] {
        return this.teamMembers?.filter(member => !member.is_disabled) || [];
    }

    @computed
    get activeSessionConfigs(): ISessionConfig[] {
        return this.scheduleSessionConfigs?.filter(session => {
            return session.enabled;
        });
    }

    getSessionConfigById(id: string) {
        return this.scheduleSessionConfigs.find(
            sessionConfig => sessionConfig._id === id
        );
    }

    @computed
    get sortedProjectTypes(): IProjectType[] {
        return sortProjectTypes(this.project_types);
    }

    @computed
    get sortedVendorEmails(): IVendorEmail[] {
        return this.vendor_emails.slice().sort((a, b) => {
            const titleA = a.title ?? '';
            const titleB = b.title ?? '';
            return titleA.localeCompare(titleB);
        });
    }

    @computed
    get vendorEmails(): IVendorEmail[] {
        const {
            userStore: { currUser }
        } = this.globalContext;

        return this.vendor_emails?.map(email => {
            const emailContent = email.dataOnly ? email.dataOnly() : email;
            return {
                ...emailContent,
                html_body: processEmailForEdit({
                    email: email.html_body,
                    emailSignature: currUser?.emailSignature
                })
            };
        });
    }

    @computed
    get fallbackHeaderImage() {
        return this.images?.length && this.images[0];
    }

    @computed
    get defaultHeaderImage() {
        return (
            this.images.find(
                image => image._id === this.default_cover_image_id
            ) ?? this.fallbackHeaderImage
        );
    }

    @computed
    get wasInitialUrlClaimed() {
        return !!this.company_url_prefixes?.length;
    }

    @computed
    get isPhotographer() {
        return this.company_type === 'photographer';
    }

    @action
    addImage(image: IImage) {
        this.images.push(image);
    }

    @action
    addImages(images: IImage[]) {
        this.images.push(...images);
    }

    @action
    setImages(images: IImage[]) {
        this.images = images;
    }

    @action
    removeImage(image: IImage) {
        this.images = this.images.filter(i => i !== image);
    }

    @action
    removeAsset(id: string) {
        this.fileAssets = this.fileAssets.filter(i => i._id !== id);
    }

    @action
    setFileAssets(options) {
        this.fileAssets = options;
    }

    @action
    updateLeadSources(options) {
        this.lead_sources = options;
    }

    @action
    getLeadSourceByLabel(label) {
        return this.lead_sources.find(leadSource => leadSource.label == label);
    }

    @action
    updateProjectTypes(options) {
        this.project_types = options;
    }

    @action
    addArchiveReasonIfNew(reason?: string) {
        const exists =
            !!reason &&
            this.archive_reasons.some(
                r => r.toLowerCase() === reason.toLowerCase()
            );

        if (!exists) {
            this.archive_reasons.push(reason);
        }
    }

    @action
    updateServiceUnits(serviceUnits: string[]) {
        this.service_units = serviceUnits;
    }

    @action
    updateDefaultProjectType(projectTypeLabel) {
        this.default_project_type_label = projectTypeLabel;
    }

    @action
    updateUserPermissions(userPermissions: IUserPermissions[]) {
        this.user_permissions = userPermissions;
    }

    @action
    markFontAsDeleted(fontName) {
        const font = this.company_fonts.find(f => f.name === fontName);
        if (font) {
            font.is_deleted = true;
        }
    }

    @action
    addOrUpdateFont(font, fileName) {
        const font_to_update = this.company_fonts.find(
            f => fileName === f.name
        );
        font.name = fileName;
        if (font_to_update) {
            font_to_update.url = font.url;
            font_to_update.is_deleted = false;
        } else {
            this.company_fonts.push(font);
        }
    }

    isFontDeleted(fontName) {
        const font = this.company_fonts.find(f => f.name === fontName);

        return !!font?.is_deleted;
    }

    getEmailTemplate(emailId: string) {
        return this.vendorEmails.find(email => email._id === emailId);
    }

    getBrochureTemplate(brochureId: string) {
        return this.brochures.find(brochure => {
            return brochure._id === brochureId;
        });
    }

    getQuestionnaireTemplate(questionnaireId: string) {
        return this.questionnaires.find(questionnaire => {
            return questionnaire._id === questionnaireId;
        });
    }

    getFlowTemplate(flowId: string) {
        return this.flows.find(flow => {
            return flow._id === flowId;
        });
    }

    getBlockTemplate(blockId: string) {
        return this.block_templates.find(block => {
            return block._id === blockId;
        });
    }

    getBlockTemplatesByType(blockType: TFlowTag) {
        let templates = this.block_templates;

        if (blockType !== undefined) {
            templates = this.block_templates.filter(template =>
                template.tags?.includes(blockType)
            );
        }

        return templates.sort(AtoZcomparator);
    }

    @computed
    get hasCompanyLogo() {
        return (
            this.logo_image?.original_url?.indexOf('yourlogohere.jpg') === -1
        );
    }

    @computed
    get defaultProjectType() {
        return (
            this.default_project_type_label ||
            (this.is_non_event_type ? EventTypes.CORPORATE : EventTypes.WEDDING)
        );
    }

    @computed
    get hasCompanyIcon() {
        return (
            !this.hasDefaultCompanyIcon() &&
            !!this.icon_image?.cloudinary_public_id
        );
    }

    @computed
    get logoOrIconURL() {
        return (
            (this.hasCompanyLogo && this.logo_image?.original_url) ||
            (this.hasCompanyIcon && this.icon_image?.url) ||
            null
        );
    }

    @computed
    get hasLogoOrIcon() {
        return !!this.logoOrIconURL;
    }

    @computed
    get userPermissionsByTeamMemberId() {
        return (
            this.user_permissions?.reduce((acc, userPermission) => {
                acc[userPermission.user_id] =
                    userPermission.company_permission || TeamRole.Basic;
                return acc;
            }, {} as Record<string, TeamRole>) || {}
        );
    }

    hasDefaultCompanyLogo(): boolean {
        const defaultImageName = 'yourlogohere.jpg';
        return this.logo_image?.original_url?.indexOf(defaultImageName) > -1;
    }

    hasDefaultCompanyIcon(): boolean {
        return !!this.icon_image?.is_default_company_icon;
    }

    @action
    deleteBlockTemplateLocally(blockTemplate: IFlowTemplate) {
        const blockIndex = this.block_templates.findIndex(
            block => block._id === blockTemplate._id
        );
        this.block_templates.splice(blockIndex, 1);
    }

    @action.bound
    setScheduleSessionConfigs(scheduleSessionConfigs: ISessionConfig[]) {
        this.scheduleSessionConfigs = scheduleSessionConfigs;
    }

    @action.bound
    updateScheduleSessionConfigById(
        id: string,
        updatedSessionConfig: ISessionConfig
    ) {
        const index = this.scheduleSessionConfigs.findIndex(
            item => item._id === id
        );

        this.scheduleSessionConfigs = this.scheduleSessionConfigs.map(
            (config, i) => (i === index ? updatedSessionConfig : config)
        );
    }

    @computed
    get isShareTeamMembersCalendars() {
        return this.company_settings?.share_team_members_calendar;
    }

    @computed
    get isVenue() {
        return this.company_type === CompanyTypeKeyEnum.Venue;
    }

    @computed
    get isCanadian() {
        return this.country?.toLowerCase() === 'ca';
    }

    @computed
    get currencySymbol() {
        // represents US dollar for pricing
        return this.isCanadian ? 'US$' : '$';
    }

    @computed
    get emailTemplatesForDropdown() {
        return this.vendorEmails
            .map(email => {
                return {
                    label: email.title,
                    value: email._id
                };
            })
            .sort((a, b) => a.label?.localeCompare(b.label));
    }

    @computed
    get allCompanyMembers() {
        return this.teamMembers
            ? [this.owner!, ...this.teamMembers]
            : [this.owner!];
    }

    @computed
    get membersSortedByName() {
        return this.allCompanyMembers.sort((userA, userB) =>
            userA.full_name?.localeCompare(userB.full_name)
        );
    }

    @computed
    get companyFolders() {
        return this.folders.slice().sort((a, b) => a.order - b.order);
    }

    @action
    updateCompany(company: Partial<Company>) {
        set(this, company);
    }

    @action
    updateCompanyFolders(folders: IFolder[]) {
        this.folders = folders;
    }

    @action
    setCompanyFlows(flows: IFlowTemplate[]) {
        this.flows = flows;
    }

    @action
    updateCompanyTheme(theme: IFlowTheme) {
        this.company_flow_theme = {
            styles: {
                ...theme.styles
            },
            buttons_styles: {
                ...theme.buttons_styles
            },
            questions_styles: {
                ...theme.questions_styles
            }
        };
    }

    @action
    updateCompanySettingsPermissions(role: TeamRole) {
        this.company_setting_permissions = role;
    }

    @action
    updateCompanyEmails(emails: IVendorEmail[]) {
        this.vendor_emails = emails;
    }

    @action
    updateUrlPrefixes(newUrlPrefixes: Company['company_url_prefixes']) {
        this.company_url_prefixes = newUrlPrefixes;
    }

    @action
    setCompanyBrandColor(brand_color: string) {
        this.brand_color = brand_color;
    }

    @action
    setCompanyIcon(iconImage: IImage) {
        this.icon_image = iconImage;
    }

    @action
    setCompanyLogo(publicId: string, url: string) {
        this.logo_image.cloudinary_public_id = publicId;
        this.logo_image.url = url;
        this.logo_image.original_url = url;
    }

    @action
    setMainCompanyLogo(publicId: string, url: string) {
        this.icon_image.cloudinary_public_id = publicId;
        this.icon_image.url = url;
        this.icon_image.original_url = url;
    }

    @computed
    get companyTheme(): IFlowTheme {
        return {
            ...this.company_flow_theme,
            styles: {
                ...this.company_flow_theme.styles,
                accent_color:
                    this.company_flow_theme.styles.accent_color ||
                    this.brand_color ||
                    colors.defaultBrandColor
            },
            buttons_styles: {
                ...this.company_flow_theme.buttons_styles
            },
            heading_styles: {
                ...this.company_flow_theme.heading_styles,
                font_family:
                    this.company_flow_theme.heading_styles?.font_family ||
                    this.company_flow_theme.styles.font_family,
                color:
                    this.company_flow_theme.heading_styles?.color ||
                    this.company_flow_theme.styles.color
            }
        };
    }

    @computed
    get companyThemeColors() {
        const {
            styles: {
                accent_color = this.company_flow_theme.styles.accent_color ||
                    this.brand_color ||
                    colors.defaultBrandColor,
                color,
                background_color
            },
            buttons_styles: { color: buttons_color },
            questions_styles: {
                background_color: questions_background_color,
                border_color: questions_border_color,
                warning_color: questions_warning_color,
                color: questions_color
            }
        } = this.company_flow_theme;

        return [
            background_color,
            color,
            accent_color,
            buttons_color,
            questions_color,
            questions_background_color,
            questions_warning_color,
            questions_border_color
        ];
    }

    @computed
    get isDefaultCompanyLogo() {
        const defaultImageName = 'yourlogohere.jpg';
        if (!this.logo_image || !this.logo_image.original_url) {
            return undefined;
        }
        return this.logo_image.original_url.indexOf(defaultImageName) > -1;
    }

    @computed
    get isDefaultCompanyIcon() {
        //used to work similar to isDefaultCompanyLogo, now taking from server.
        return !this.icon_image || this.icon_image.is_default_company_icon;
    }

    @computed
    get clientPortalTheme(): IClientPortalTheme {
        const companyLogo = this.isDefaultCompanyLogo ? false : this.logo_image;
        const companyIcon = this.isDefaultCompanyIcon ? false : this.icon_image;

        const defaultData: IClientPortalTheme = {
            title: this.company_name,
            background_color: '#FBFBFB',
            has_logo: !!companyLogo,
            has_icon: !!companyIcon,
            logo: {
                show: !!(companyLogo || companyIcon),
                type: companyLogo ? 'square' : 'rounded',
                logo_url: this.logo_image && this.logo_image.url,
                icon_url: this.icon_image && this.icon_image.url
            },
            brand: {
                brand_color: this.brand_color,
                text_color: this.text_color
            }
        };

        if (this.client_portal_configuration) {
            return extend(defaultData, this.client_portal_configuration);
        }

        return defaultData;
    }

    @computed
    get emailTemplates() {
        return this.vendorEmails;
    }

    @computed
    get packagesTemplates() {
        return this.vendor_packages;
    }

    @computed
    get contactFormsTemplates() {
        return this.contact_forms?.filter(
            cf => cf.contact_form_flow_type === 'default'
        );
    }

    @computed
    get giftCardsTemplates() {
        return this.contact_forms?.filter(
            cf => cf.contact_form_flow_type === 'gift_card'
        );
    }

    @computed
    get brochuresTemplates() {
        const oldBrochures = this.old_brochures?.map(brochure => ({
            ...brochure,
            isOldBrochure: true
        }));

        return [...(this.brochures || []), ...(oldBrochures || [])];
    }

    @computed
    get imagesAndIcons() {
        const { logo_image, icon_image, images: coverImages } = this;
        const images = [...coverImages];

        //  Avoid default icon image and logo image to be shown
        // isDefaultCompanyLogo and isDefaultCompanyIcon functions exist on the company_service.js file in angular

        if (logo_image?.original_url && !this.hasDefaultCompanyLogo()) {
            images.unshift(logo_image);
        }

        if (icon_image && !this.hasDefaultCompanyIcon()) {
            images.unshift(icon_image);
        }
        return images;
    }

    @computed
    get firstLibraryImage() {
        return this.imagesAndIcons[0];
    }

    get isConsultant() {
        return this.company_vertical === 'consultant';
    }

    @computed
    get hasPicTimeIntegration() {
        return !!this.has_pictime_integration;
    }

    @computed
    get hasPrismmIntegration() {
        return !!this.has_prismm_integration;
    }

    @computed
    get hasTheKnotIntegration() {
        return !!this.has_the_knot_integration;
    }

    @computed
    get johnBranchFolderId() {
        return this.folders.find(folder => folder.path === 'John Branch')?._id;
    }

    @computed
    get fullTimezone(): TFullTimezone {
        return shortTimezoneToFullTimezone[this.default_timezone];
    }

    @computed
    get isEligibleForInstantPayout() {
        return (
            this.eligible_for_instant_payouts &&
            !this.eligible_for_finance_app_payments
        );
    }

    @action
    setAllInclusiveInstantPayouts(
        enabled: boolean,
        companyCtrl: CompanyController
    ) {
        this.all_inclusive_instant_payouts = enabled;
        // Update the user both in angular and react
        // based on this comment: https://github.com/HoneyBook/honeybook-react/commit/ddbe403cb3d3e392cbc7e3b28de2f77391c606d1
        companyCtrl.updateNgCompany({
            _id: this._id,
            all_inclusive_instant_payouts: enabled
        });
    }

    @action
    setPicTimeIntegration(integrated: boolean) {
        this.has_pictime_integration = integrated;
    }

    @action
    setPrismmIntegration(integrated: boolean) {
        this.has_prismm_integration = integrated;
    }

    @action
    setDefaultCoverImage(_id: string) {
        this.default_cover_image_id = _id;
    }

    @action
    setLeadSources(leadSources: ILeadSource[]) {
        this.lead_sources = leadSources;
    }

    shouldHideProjectField(fieldName: string) {
        return (
            this.hidden_project_fields &&
            this.hidden_project_fields.indexOf(fieldName) !== -1
        );
    }

    @action
    setCustomProjectFields(updatedFields: ICustomProjectField[]) {
        this.custom_project_fields = updatedFields;
    }

    @action
    setCustomProjectFieldsV2(updatedFields: ICustomFieldSchema[]) {
        this.custom_project_fields_v2 = updatedFields;
    }

    @action
    setCustomProjectFieldV2(updatedField: ICustomFieldSchema) {
        this.custom_project_fields_v2 = this.custom_project_fields_v2.map(
            field => (field._id === updatedField._id ? updatedField : field)
        );
    }

    @action
    setCustomContactFields(updatedFields: ICustomFieldSchema[]) {
        this.custom_contact_fields = updatedFields;
    }

    @action
    setCustomContactField(updatedField: ICustomFieldSchema) {
        this.custom_contact_fields = this.custom_contact_fields.map(field =>
            field._id === updatedField._id ? updatedField : field
        );
    }

    @action.bound
    setQuickbooksIntegration(integration: IQuickbooksIntegration) {
        this.quickbooks_integration = integration;
    }

    @computed
    get isConnectedToQuickBooks() {
        return !!this.quickbooks_integration?.connected_at;
    }

    @computed
    get pipelineViews() {
        return this.pipeline_views?.filter(
            view => view.type === 'main' || view.type === 'custom'
        );
    }

    @computed
    get mainPipelineViewId() {
        return this.pipelineViews?.find(view => view.type === 'main')?._id;
    }

    @action
    updateClientPortalThemeLocally({
        background_color,
        show_logo,
        logo_type,
        title,
        client_portal_branding
    }: UpdatedClientPortalTheme) {
        this.client_portal_configuration.logo.show = show_logo;
        this.client_portal_configuration.logo.type = logo_type;
        this.client_portal_configuration.background_color = background_color;
        this.client_portal_configuration.title = title;
        if (client_portal_branding) {
            this.client_portal_configuration.client_portal_branding.sidenav_theme.background_color =
                client_portal_branding.sidebar_color;
            this.client_portal_configuration.client_portal_branding.buttons_styles.color =
                client_portal_branding.button_text_color;
            this.client_portal_configuration.client_portal_branding.buttons_styles.border_radius =
                client_portal_branding.button_border_radius;
            this.client_portal_configuration.client_portal_branding.buttons_styles.font_family =
                client_portal_branding.button_font;
            this.client_portal_configuration.client_portal_branding.styles.background_color =
                client_portal_branding.project_background_color;
            this.client_portal_configuration.client_portal_branding.styles.accent_color =
                client_portal_branding.accent_color;
            this.client_portal_configuration.client_portal_branding.heading_styles.font_family =
                client_portal_branding.heading_font;
        }
    }

    getAutomationStatus(automationType: WorkflowAutomationType): boolean {
        const automationInfo = this.workflow_automation_infos?.find(
            ({ automation_type }) => automation_type === automationType
        );

        return automationInfo?.is_automation_on;
    }

    @action
    setAutomation(automation: IWorkflowAutomationInfo) {
        const index = this.workflow_automation_infos?.findIndex(
            ({ automation_type }) =>
                automation_type === automation.automation_type
        );
        if (index > -1) {
            this.workflow_automation_infos[index] = {
                ...this.workflow_automation_infos[index],
                ...automation
            };
        } else {
            this.workflow_automation_infos.push(automation);
        }
    }

    @computed
    get companySettings() {
        return this.company_settings;
    }

    @action.bound
    setIsFlowThumbnailVisibleToClient(
        is_flow_thumbnail_visible_to_client: boolean
    ) {
        this.is_flow_thumbnail_visible_to_client =
            is_flow_thumbnail_visible_to_client;
    }

    @action.bound
    setSmsEnabledByType(
        smsType: TSmsType,
        enabled: boolean,
        companyCtrl: CompanyController
    ) {
        if (!this.hasOwnProperty(smsType)) {
            return;
        }
        this[smsType] = enabled;
        companyCtrl.updateNgCompany({
            [smsType]: enabled
        });
    }

    @action.bound
    setSmsPreferences(
        smsPreferences: ICompanySmsPreferences,
        companyCtrl: CompanyController
    ) {
        this.sms_payment_reminders_enabled =
            smsPreferences.sms_payment_reminders_enabled;
        this.sms_meeting_reminders_enabled =
            smsPreferences.sms_meeting_reminders_enabled;
        this.client_2fa_sms_enabled = smsPreferences.client_2fa_sms_enabled;
        this.sms_enabled = smsPreferences.sms_enabled;
        companyCtrl.updateNgCompany({
            sms_payment_reminders_enabled:
                smsPreferences.sms_payment_reminders_enabled,
            sms_meeting_reminders_enabled:
                smsPreferences.sms_meeting_reminders_enabled,
            client_2fa_sms_enabled: smsPreferences.client_2fa_sms_enabled,
            sms_enabled: smsPreferences.sms_enabled
        });
    }

    @action.bound
    setOwnerNotificationByType(
        type: TCompanyOwnerNotificationType,
        isOn: boolean
    ) {
        const targetIndex = this.company_owner_notifications.findIndex(
            ({ notification_type }) => notification_type === type
        );

        if (targetIndex > -1) {
            this.company_owner_notifications[targetIndex].is_notification_on =
                isOn;
        } else {
            this.company_owner_notifications.push({
                _id: 'temp_id',
                notification_type: type,
                notification_type_cd:
                    COMPANY_OWNER_NOTIFICATION_TYPE_TO_CD_MAP[type],
                is_notification_on: isOn
            });
        }
    }

    @action.bound
    setShareTeamMemberCalendarCompanySetting(isOn: boolean) {
        this.company_settings.share_team_members_calendar = isOn;
    }

    @action.bound
    removeTeamMemberById(id: string) {
        this.team_members = this.team_members.filter(({ _id }) => _id !== id);
    }

    @action
    setIsN8NUserInitialized(initialized: boolean) {
        this.n8n_user_initialized = initialized;
    }

    shouldShowMembership() {
        return this.show_membership_page !== false;
    }

    @computed
    get sortedLeadSources() {
        const editableLeadSources =
            this.lead_sources?.filter(leadSource => leadSource.editable) || [];
        const nonEditableLeadSources =
            this.lead_sources?.filter(leadSource => !leadSource.editable) || [];

        return [...editableLeadSources, ...nonEditableLeadSources];
    }

    @action.bound
    setInquiryFollowUp(inquiryFollowUp: InquiryFollowUpResponse) {
        this.inquiry_follow_up = inquiryFollowUp;
    }

    get companySpaces() {
        return this.company_spaces;
    }

    @action
    setCompanySpaces(spaces: TCompanySpaceUpdate[]) {
        this.company_spaces = spaces;
    }

    @action
    setContactEnrichmentEnabled(enabled: boolean) {
        this.contact_enrichment_enabled = enabled;
    }

    @action
    setAiContactFollowupSuggestionsEnabled(enabled: boolean) {
        this.ai_contact_followup_suggestions_enabled = enabled;
    }

    @action
    setContactLastInteractionAutoUpdateEnabled(enabled: boolean) {
        this.contact_last_interaction_auto_update_enabled = enabled;
    }

    @action.bound
    setSmsPaymentReminderEnabled(enabled: boolean) {
        this.sms_payment_reminders_enabled = enabled;
    }

    @computed
    get questionnairesForDropdown() {
        return this.questionnaires.map(questionnaire => ({
            label: questionnaire.title!,
            value: questionnaire._id
        }));
    }

    @computed
    get shouldShowPrismmProjectTool() {
        const exposedVerticals: TCompanyTypes[] = ['venue', 'event_planner'];
        return (
            this.hasPrismmIntegration ||
            exposedVerticals.includes(this.company_type)
        );
    }

    @computed
    get sortedPipelineStages() {
        return this.user_pipeline_stages
            .slice()
            .sort((a, b) =>
                a.order && b.order ? (a.order > b.order ? 1 : -1) : 0
            );
    }

    @action
    updateCoverImageInCompany = (image: IImage) => {
        const updatedImages = this.images.map(img =>
            img._id === image._id ? { ...img, ...image } : img
        );
        this.setImages(updatedImages);
    };

    @computed
    get logoAndIconImages(): IImage[] {
        const images: IImage[] = [];
        if (
            this.logo_image?.cloudinary_public_id &&
            !this.hasDefaultCompanyLogo()
        ) {
            images.push(this.logo_image);
        }
        if (
            this.icon_image?.cloudinary_public_id &&
            !this.hasDefaultCompanyIcon()
        ) {
            images.push(this.icon_image);
        }
        return images;
    }

    toCompanyResponse() {
        return JSON.parse(tryParseToJsonString(this).json) as ICompanyResponse;
    }
}
